import env from './AppEnv';
import translate, { transExists } from './Translator';
/**
 * Capability names Paloma itself defines, mirroring the constants on
 * ch.astina.paloma.common.capabilities.PalomaCapabilities. An application may advertise others -
 * a newer Paloma version, or one the project defines - so this is not a closed set: everything
 * here takes a plain string, and a name this UI does not know is carried through and simply not
 * acted on.
 */
export const Capability = {
    STATUS: 'status',
    CONFIG: 'config',
    DATA: 'data',
    CONDUITS: 'conduits',
    DATA_NETWORK: 'data_network',
    TASKS: 'tasks',
    OPERATIONS: 'operations',
    MONITORS: 'monitors',
    INTEGRATIONS: 'integrations',
    INPUT: 'input',
    OUTPUT: 'output',
    IMPORT_PROFILES: 'import_profiles',
    EXPORT_PROFILES: 'export_profiles',
    EVENTS: 'events',
    MAPPINGS: 'mappings',
    SECRETS: 'secrets',
    DYNAMIC_ENTITIES: 'dynamic_entities',
    DYNAMIC_CONDUITS: 'dynamic_conduits',
    CREDENTIALS: 'credentials',
    GENERIC_CONNECTORS: 'generic_connectors',
    PROCESS_REPORTS: 'process_reports',
    DATA_TRANSFER_LOGS: 'data_transfer_logs',
    LOGS: 'logs',
    ACTIVITY_LOGS: 'activity_logs',
    ACTIVITIES: 'activities',
    NOTIFICATIONS: 'notifications',
    STATISTICS: 'statistics',
    USERS: 'users',
    TRANSLATIONS: 'translations',
    DOCUMENTS: 'documents',
    FILES: 'files',
    IMAGES: 'images',
    CONTENT: 'content',
    DOCS: 'docs',
    EVENT_SAGA_ORCHESTRATION: 'event_saga_orchestration',
    FORMS: 'forms',
    USER_SETTINGS: 'user_settings',
    SHOP_ORDERS: 'shop_orders',
    SHOP_DATA_CATALOG: 'shop_data_catalog',
    SHOP_DATA_CUSTOMERS: 'shop_data_customers',
    SHOP_DATA_PROMOTIONS: 'shop_data_promotions',
    SHOP_CATALOG: 'shop_catalog',
    SHOP_CUSTOMERS: 'shop_customers',
    SHOP_CHECKOUT: 'shop_checkout',
    SHOP_PROMOTIONS: 'shop_promotions',
    DELIVERY_OPTIONS: 'delivery_options',
    EDI_LIGHT: 'edi_light',
};
/**
 * Names the application storing the configuration of the input/output capability - not the
 * application running it.
 */
export const ATTRIBUTE_DATA_APPLICATION = 'data_application';
/**
 * Attribute of the logs capability: where the log statements are read from, `local` or `kibana`.
 */
export const ATTRIBUTE_STORAGE = 'storage';
/**
 * The capability's name for a heading or a list, falling back to the raw name - all this UI has
 * for a capability it does not know: one from a newer Paloma version, or one the project defines.
 *
 * Not `capability.<name>`, which is the lower-case wording meant for use inside a sentence.
 */
export function capabilityLabel(capability) {
    return translate(`capability.${capability}.label`, undefined, capability);
}
/**
 * The capability's one-sentence description, or null when there is none - `trans` treats an empty
 * default as no default and would hand back the message code, which is not something to show.
 */
export function capabilityDescription(capability) {
    const key = `capability.${capability}.description`;
    return transExists(key) ? translate(key) : null;
}
/**
 * The advertised applications. `window._ENV` is an untyped blob, so a project supplying its own
 * without `applications` would otherwise make this module throw - and env.js is evaluated before
 * the bundle, so that takes the whole UI down rather than just hiding a feature.
 */
function all() {
    return env.applications ?? [];
}
/**
 * Returns every capability name any application advertises, sorted and without duplicates. Names
 * this UI does not know are included: they are what the applications actually reported, and
 * dropping them is exactly what the open-string design forbids.
 */
function names() {
    return [...new Set(all().flatMap(application => Object.keys(application.capabilities)))].sort();
}
/**
 * Returns true if any application advertises the capability.
 */
function has(capability) {
    return applications(capability).length > 0;
}
/**
 * Returns true if the named application advertises the capability.
 *
 * For the features the cockpit reaches per application rather than globally - the data views are
 * addressed as `/data/:app/...` - where "does anyone provide this" is the wrong question.
 */
function applicationHas(application, capability) {
    return all().some(app => app.name === application && capability in app.capabilities);
}
/**
 * Returns the names of every application advertising the capability, ordered as the backend
 * listed them (by application name), so that "the first one" is stable across page loads.
 */
function applications(capability) {
    return all()
        .filter(application => capability in application.capabilities)
        .map(application => application.name);
}
/**
 * Returns the first application advertising the capability.
 *
 * Only for the features that can point at a single application; where several qualify this
 * silently picks one, so the caller is relying on cockpit-ui's CapabilityConflicts to warn
 * about it. Use `applications()` wherever the UI can handle several.
 */
function application(capability) {
    return applications(capability)[0];
}
/**
 * Returns the first value reported for the capability's attribute - see the caveat on
 * `application()`. Defined as the first of `attributeValues()` so that what the UI uses and what
 * CapabilityConflicts reports as being used cannot disagree.
 */
function attribute(capability, name) {
    return attributeValues(capability, name)[0];
}
/**
 * Returns every value reported for the capability's attribute, one per application advertising
 * both, in env order and without duplicates. Applications advertising the capability without the
 * attribute are skipped rather than yielding a blank.
 */
function attributeValues(capability, name) {
    const values = all()
        .filter(app => capability in app.capabilities)
        .map(app => app.capabilities[capability][name])
        .filter(value => value !== undefined && value !== null && value !== '');
    return [...new Set(values)];
}
const capabilities = {
    names,
    has,
    applicationHas,
    applications,
    application,
    attribute,
    attributeValues,
};
export default capabilities;
